import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import { applySnapshot, cast, flow, Instance, types } from 'mobx-state-tree';
import { killNulls, splitPhone } from 'utils';
import { IArea, IPhone } from 'types';
import { Areas, Tenants } from '../../http-requests';
import { ISelectValue } from 'components/ui';

import { PhoneModel, RequestStateModel } from './general-models';

export interface IMember extends Instance<typeof MemberBaseModel> {}

const StatusesModel = types.model('StatusesModel', {
  areaCommunications: types.optional(RequestStateModel, {}),
  areaData: types.optional(RequestStateModel, {}),
  members: types.optional(RequestStateModel, {}),
  createOtherMember: types.optional(RequestStateModel, {}),
});

const AssessmentModel = types.model('AssessmentModel', {
  up: types.optional(types.number, 0),
  down: types.optional(types.number, 0),
});

const RequestsModel = types.model({
  areaCommunications: types.optional(RequestStateModel, {}),
  areaData: types.optional(RequestStateModel, {}),
});

const CommunicationModel = types.model('CommunicationModel', {
  id: types.optional(types.identifier, ''),
  description: types.optional(types.string, ''),
  is_active: types.optional(types.boolean, false),
  is_bonded: types.optional(types.maybeNull(types.boolean), false),
  meter_type: types.optional(types.string, ''),
  room: types.optional(types.maybeNull(types.string), null),
  standpipe: types.optional(types.maybeNull(types.string), null),
});

export const HouseModel = types.model('HouseModel', {
  id: types.optional(types.string, ''),
  address: types.optional(types.string, ''),
  fias_addrobjs: types.optional(types.array(types.string), []),
});

const AreaDataModel = types
  .model('AreaDataModel', {
    id: types.optional(types.identifier, ''),
    area_balconies: types.optional(types.number, 0),
    area_common: types.optional(types.number, 0),
    area_corridors: types.optional(types.number, 0),
    area_cupboards: types.optional(types.number, 0),
    area_height: types.optional(types.number, 0),
    area_living: types.optional(types.number, 0),
    area_loggias: types.optional(types.number, 0),
    area_pantries: types.optional(types.number, 0),
    area_total: types.optional(types.number, 0),
    area_total_history: types.optional(
      types.array(types.frozen<{ [key: string]: any }>()),
      []
    ),
    cadastral_number: types.optional(types.maybeNull(types.string), null),
    common_property: types.optional(types.boolean, false),
    floor: types.optional(types.maybeNull(types.number), 0),
    gis_uid: types.optional(types.maybeNull(types.string), null),
    has_bath: types.optional(types.boolean, false),
    has_boiler: types.optional(types.boolean, false),
    has_ch: types.optional(types.boolean, false),
    has_chute: types.optional(types.boolean, false),
    has_cw: types.optional(types.boolean, false),
    has_hw: types.optional(types.boolean, false),
    has_lift: types.optional(types.boolean, false),
    house: types.optional(HouseModel, {}),
    intercom: types.optional(types.string, ''),
    is_allowed_meters: types.optional(types.boolean, false),
    is_parking: types.optional(types.boolean, false),
    is_rent: types.optional(types.boolean, false),
    is_shared: types.optional(types.maybeNull(types.boolean), null),
    num_alt: types.optional(types.maybeNull(types.string), null),
    num_desc: types.optional(types.maybeNull(types.string), null),
    num_letter: types.optional(types.maybeNull(types.string), null),
    number: types.optional(types.number, 0),
    parent_area: types.optional(
      types.maybeNull(types.frozen<{ [key: string]: any }>()),
      null
    ),
    porch: types.optional(types.maybeNull(types.string), ''),
    property_type: types.optional(types.string, ''),
    rosreestr: types.optional(
      types.maybeNull(types.frozen<{ [key: string]: any }>()),
      null
    ),
    stove_type: types.optional(types.maybeNull(types.string), null),
    str_number: types.optional(types.string, ''),
    str_number_full: types.optional(types.string, ''),
    _type: types.optional(types.array(types.string), []),
  })
  .views((self) => ({
    // Общая информация о квартире, приведенная к типу IArea
    get dataIAreaFormat(): IArea {
      return {
        house: cloneDeep(self.house),
        id: self.id,
        is_shared: self.is_shared,
        number: self.number,
        str_number: self.str_number,
        str_number_full: self.str_number_full,
        _type: cloneDeep(self._type),
      };
    },
  }));

export const MemberBaseModel = types
  .model('MemberModel', {
    id: types.optional(types.string, ''),
    _type: types.optional(types.array(types.string), []),
    assessment: types.optional(types.maybeNull(AssessmentModel), {}),
    has_access: types.optional(types.boolean, false),
    str_name: types.optional(types.string, ''),
    email: types.optional(types.maybeNull(types.string), ''),
    registered: types.optional(types.boolean, false),
    living: types.optional(types.boolean, false),
    is_owner: types.optional(types.boolean, false),
    phones: types.optional(types.array(PhoneModel), []),
    is_responsible: types.optional(types.boolean, false),
    is_renter: types.optional(types.boolean, false),
    is_coop_member: types.optional(types.boolean, false),
    is_archive: types.optional(types.boolean, false),
    number: types.optional(types.string, ''),
    property_share: types.optional(types.array(types.number), []),
    ownership: types.optional(
      types.maybeNull(types.frozen<{ [propName: string]: any }>()),
      null
    ),
    can_vote: types.optional(types.boolean, false),
  })
  .views((self) => ({
    get isMember() {
      return self._type.includes('Tenant');
    },
    get hasPhones() {
      return !!self.phones.length;
    },
  }))
  .actions((self) => ({
    addPhone: flow(function* (newPhone: string) {
      const { code, number } = splitPhone(newPhone);
      const newPhones = [
        ...self.phones,
        {
          code,
          number,
          str_number: newPhone,
        },
      ];

      try {
        const response = yield Tenants.SetTenantContacts(self.id, {
          phones: newPhones,
        });
        self.phones.replace(response.phones);

        return response.phones;
      } catch (error) {
        console.error(error);
      }
    }),
    setPhones(phones: IPhone[]) {
      self.phones = cast(phones);
    },
  }));

const MemberModel = types.snapshotProcessor(MemberBaseModel, {
  preProcessor(sn: IMember) {
    return {
      ...sn,
      assessment: sn.assessment ?? {},
    };
  },
});

export const AreasStore = types
  .model({
    loading: types.optional(RequestsModel, {}),
    statuses: types.optional(StatusesModel, {}),
    areaCommunications: types.optional(types.array(CommunicationModel), []),
    areaData: types.optional(AreaDataModel, {}),
    members: types.optional(types.array(MemberModel), []),
  })
  .volatile(() => ({
    areaDataResultsGeneration: 0,
    areaDataLoadingGeneration: 0,
  }))
  .views((self) => ({
    getCommunicationInfo(id: string) {
      return (
        find(self.areaCommunications, { id: id }) ?? {
          description: 'Неизвестный счётчик',
        }
      );
    },
    get memberOptions(): Array<ISelectValue> {
      return self.members.map((member) => ({
        value: member.id,
        label: member.str_name,
      }));
    },
  }))
  .actions((self) => ({
    setMembers(newList: Array<IMember>) {
      self.members.replace(newList);
    },
    clearAreaCommunications() {
      self.statuses.areaCommunications = cast({});
      self.areaCommunications = cast([]);
    },
    clearMembers() {
      self.members.clear();
    },
    clearAll() {
      self.areaDataResultsGeneration += 1;
      self.areaDataLoadingGeneration += 1;
      applySnapshot(self, {});
    },
    getAreaCommunications: flow(function* (id: string) {
      try {
        self.statuses.areaCommunications.fulfilled = false;
        self.statuses.areaCommunications.error = false;
        self.statuses.areaCommunications.pending = true;

        self.areaCommunications = yield Areas.GetAreaCommunications(id);

        self.statuses.areaCommunications.fulfilled = true;

        return self.areaCommunications;
      } catch (error) {
        self.statuses.areaCommunications.error = true;
        console.error(error);
      } finally {
        self.statuses.areaCommunications.pending = false;
      }
    }),
    getAreaData: flow(function* (id: string, shouldApply?: () => boolean) {
      self.areaDataResultsGeneration += 1;
      self.areaDataLoadingGeneration += 1;
      const resultsGeneration = self.areaDataResultsGeneration;
      const loadingGeneration = self.areaDataLoadingGeneration;
      const canApply = () =>
        resultsGeneration === self.areaDataResultsGeneration &&
        (shouldApply?.() ?? true);
      const canFinalize = () =>
        loadingGeneration === self.areaDataLoadingGeneration;

      applySnapshot(self.statuses.areaData, {});

      try {
        self.statuses.areaData.loadingStates();

        const response = yield Areas.GetAreaById(id);

        if (canApply()) {
          self.areaData = response;
          self.statuses.areaData.successStates();
        }

        return self.areaData;
      } catch (error) {
        if (canApply()) {
          self.statuses.areaData.errorStates();
          console.error(error);
        }
      } finally {
        if (canFinalize()) {
          self.statuses.areaData.finallyStates();
        }
      }
    }),
    getMembers: flow(function* (areaId: string, other_tenants: boolean) {
      try {
        self.statuses.members.loadingStates();

        const response: { results: { members: IMember[] }[] } =
          yield Areas.GetFamiliesByArea({
            account__area__id: areaId,
            other_tenants_include: other_tenants,
          });

        const allMembers = response.results.map((el) => el.members).flat();
        self.members.replace(allMembers);
        self.statuses.members.successStates();

        return self.members;
      } catch (error) {
        self.statuses.members.errorStates();
        console.error(error);
      } finally {
        self.statuses.members.finallyStates();
      }
    }),
    createOtherMember: flow(function* (params: any) {
      try {
        self.statuses.createOtherMember.loadingStates();

        const newParams = killNulls(params);
        const newMember = yield Tenants.CreateOtherTenant(newParams);
        self.members.push(MemberBaseModel.create({ ...newMember }));
        self.statuses.createOtherMember.successStates();

        return newMember;
      } catch (error) {
        console.error(error);
        self.statuses.createOtherMember.errorStates();

        throw error;
      } finally {
        self.statuses.createOtherMember.finallyStates();
      }
    }),
    clearAreaData() {
      self.areaDataResultsGeneration += 1;
      self.areaDataLoadingGeneration += 1;
      self.statuses.areaData = cast({});
      self.loading.areaData = cast({});
      self.areaData = cast({});
    },
  }));
